import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SecurityContext,
  SimpleChanges,
  inject,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslocoPipe } from '@jsverse/transloco';
import { LoadingIndicatorDirective } from '@zero/shared/ui-directives';
import { ButtonComponent } from '@zero/shared/ui-web-design-system';
import { Ticket, TicketThreadEntry, TicketThreadEntryAttachment, UserDataModel } from '@zero/shared/util-models';
import { TicketDialogService, UserMessagesHolderService } from '@zero/shared/util-web-app-services';
import { TicketCreationState } from '../../models/ticket-creation-state';
import { AttachmentLinkPipe } from '../../pipes/attachment-link/attachment-link.pipe';
import { AttachmentSizePipe } from '../../pipes/attachment-size/attachment-size.pipe';
import { EntryTypePipe } from '../../pipes/entry-type/entry-type.pipe';
import { CreateTicketComponent } from '../create-ticket/create-ticket.component';

@Component({
  selector: 'tickets-show-ticket-thread',
  templateUrl: './show-ticket-thread.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LoadingIndicatorDirective,
    CreateTicketComponent,
    DatePipe,
    AttachmentSizePipe,
    EntryTypePipe,
    ButtonComponent,
    AttachmentLinkPipe,
    TranslocoPipe,
  ],
})
export class ShowTicketThreadComponent implements OnChanges {
  @Input() public ticket: Ticket;
  @Input() public lastTicketLoad: number;
  @Input() public currentUser: UserDataModel = {
    username: 'not set',
    email: 'not set',
  };
  @Output() public ticketChanged = new EventEmitter<Ticket>();

  public thread: TicketThreadEntry[] = [];
  public dialogMode: 'createComment';
  public errors: string[] = [];
  public ongoingRequest = false;
  public ticketCloseConfirm = false;
  public showTicketClosedInfo = false;

  readonly #ticketDialogService = inject(TicketDialogService);
  readonly #sanitizer = inject(DomSanitizer);
  readonly #msgService = inject(UserMessagesHolderService);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ticket'] || changes['lastTicketLoad']) {
      this.thread = this.createThreadFromTicket(this.ticket);
    }
  }

  private createThreadFromTicket(t: Ticket): TicketThreadEntry[] {
    const allEntries: TicketThreadEntry[] = [];

    const createEntry = (
      time: string,
      content: string,
      author: string,
      type: 'ticket' | 'response' | 'comment',
      attachments?: any[],
      resolutionContent?: string,
    ) => {
      const saveContent = this.#sanitizer.sanitize(SecurityContext.HTML, content);
      const entry = new TicketThreadEntry(time, saveContent, author, type);
      if (attachments) {
        entry.attachments = attachments.map(
          (att) => new TicketThreadEntryAttachment(att.name, att.id, att.size, att.threadId, att.isAgent),
        );
      }
      if (resolutionContent) {
        entry.resolutionContent = this.#sanitizer.sanitize(SecurityContext.HTML, resolutionContent);
      }
      return entry;
    };

    if (t.deskTicket) {
      let author;
      if (t.deskTicket.contact) {
        author = `${t.deskTicket.contact.firstName} ${t.deskTicket.contact.lastName || ''}`.trim();
      } else if (t.type === 'OTHER') {
        author = this?.currentUser?.email;
      } else {
        author = 'finanzen.net zero';
      }

      const attachments = t.deskTicket.descAttachments?.map((att) => ({
        ...att,
        isAgent: t.author === 'System',
      }));

      const entry = createEntry(t.deskTicket.createdTime, t.deskTicket.description, author, 'ticket', attachments, t.deskTicket.resolution);
      allEntries.push(entry);
    }

    if (t.deskComments) {
      t.deskComments.forEach((comment) => {
        const attachments = comment.attachments?.map((att) => ({
          ...att,
          isAgent: comment?.commenter?.type === 'AGENT',
        }));
        const entry = createEntry(comment.commentedTime, comment.content, comment.commenter.name, 'comment', attachments);
        allEntries.push(entry);
      });
    }

    if (t.deskThreads) {
      t.deskThreads.forEach((thread) => {
        const attachments = thread.attachments?.map((att) => ({
          ...att,
          threadId: thread.id,
        }));
        const entry = createEntry(thread.createdTime, thread.content, thread.author.name, 'response', attachments);
        allEntries.push(entry);
      });
    }

    return allEntries.sort((a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime());
  }

  public createComment(): void {
    this.dialogMode = 'createComment';
  }

  public commentCreationStateUpdate(event: TicketCreationState): void {
    if (event.status === 'canceled') {
      this.dialogMode = undefined;
    } else if (event.status === 'finished') {
      this.dialogMode = undefined;
      this.ticketChanged.emit(this.ticket);
    }

    if (event.errors) {
      this.errors = event.errors;
    }
  }

  public updateTicketStatus(status: 'Closed' | 'Open'): void {
    this.errors = [];
    this.#msgService.clearAllMessages();
    this.ongoingRequest = true;
    this.#ticketDialogService.updateTicketStatus(this.ticket.deskTicket.id, status).subscribe((result) => {
      this.ongoingRequest = false;
      if (result.success) {
        if (status === 'Open') {
          this.ticket.status = 'OPEN';
        } else {
          this.ticket.status = 'CLOSED';
          this.showTicketClosedInfo = true;
        }
        this.ticketChanged.emit(this.ticket);
      } else {
        this.errors.push(result.message);
      }
    });
  }
}
